<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import SvgIcon from '../SvgIcon.vue';
import { paramIconMap } from './constants';
import { useUpdateQuery } from './composables';

const props = defineProps({
    name: {
        type: String,
        required: true,
    },
    selectedIndex: {
        type: [Number, null],
        default: null,
    },
});

defineEmits(['click']);

const store = useStore();

const show = ref(true);

const defaultSuggestions = computed(() => store.state.defaultSuggestions || {});

const itemsByType = computed(() => {
    return Object.entries(defaultSuggestions.value).reduce((acc, [type, data]) => {
        if (data.suggestions.length > 0) {
            acc[type] = data.suggestions;
        }
        return acc;
    }, {});
});

const typeTitles = computed(() => {
    return Object.entries(defaultSuggestions.value).reduce((acc, [type, data]) => {
        acc[type] = data.title;
        return acc;
    }, {});
});

function getFlatIndex(groupIndex, index) {
    return Object.values(itemsByType.value).slice(0, groupIndex).flat().length + index;
}

// This usually comes from the useAutocompletions composable,
// but since this is not shown together with autocompletion, we can have an empty completionUpdateFunction
const requestCompletionsUpdate = () => {};
const {
    handleSelectSuggestion,
    handleRemoveChosenQueryParam,
    // note requestCompletionUpdate is just an empty function. See comment above.
} = useUpdateQuery(requestCompletionsUpdate, store);

/**
 * @param {{ id: string, duration: number }} featuredArea
 */
function createClickHandler(type, item) {
    return () => {
        const metaData = { type };

        if (isInSearchQuery(type, item.value)) {
            handleRemoveChosenQueryParam(item, type);
        } else {
            handleSelectSuggestion(item, metaData);
        }
        return {
            emitEvent: {
                name: 'suggestionSelected',
                params: {},
            },
        };
    };
}

const clickHandlers = computed(() => {
    return Object.entries(itemsByType.value).map(([type, items]) => {
        return items.map((item) => createClickHandler(type, item));
    }).flat();
});

defineExpose({
    numSelectableItems: clickHandlers.value.length,
    clickHandlers,
    name: props.name,
    show,
    hasContent: Object.keys(itemsByType.value).length > 0,
});

function isInSearchQuery(type, value) {
    return store.getters.workingQuery[type]?.includes(value);
}

</script>

<template>
    <div v-show="show">
        <div
            class="jobsearch-dropdown-section"
            v-for="items, type, groupIndex in itemsByType"
            :key="type"
        >
            <div class="category-header jobsearch-dropdown-section__header">
                {{ typeTitles[type] }}
            </div>
            <ul>
                <li
                    class="jobsearch-dropdown-section__suggestion d-flex justify-content-between"
                    v-for="item, index in items"
                    :key="type + item.value"
                    :class="{ 'jobsearch-dropdown-section__suggestion--selected': selectedIndex === getFlatIndex(groupIndex, index) }"
                    @click="$emit('click', getFlatIndex(groupIndex, index))"
                >
                    <div>
                        <SvgIcon
                            :name="paramIconMap[type]"
                            size="xs"
                            role="presentation"
                            class="mr-2"
                        />
                        <span :class="{'font-weight-bold': isInSearchQuery(type, item.value)}">
                            {{ item.text }}
                        </span>
                    </div>
                    <SvgIcon
                        size="mini"
                        class="search-parameter-selected-icon"
                        name="button-check"
                        v-if="isInSearchQuery(type, item.value)"
                    />
                </li>
            </ul>
        </div>
    </div>
</template>
